import React from "react";
import s from "./style.module.css";
import { groups, mean, extent } from "d3-array";
import { scaleLinear, scaleOrdinal } from "d3-scale";
import { line, curveLinearClosed, curveLinear } from "d3-shape";
import { interpolateDiscrete } from "d3-interpolate";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import commuting_json from "./../../data/jakarta_commuting.json";
import home_json from "./../../data/jakarta_time_at_home.json";
import low_wealth_json from "./../../data/jakarta_low_wealth.json";
import events_json from "./../../data/jakarta_events.json";

const margin = {
  top: 10,
  right: 180,
  bottom: 10,
  left: 50,
};

const N = 14;

// function pctChange (values, accessor) {
//   return values.reduce((acc, cur, i, arr) => {
//     if (i > 0) {
//       acc.push((accessor(cur) - accessor(arr[i - 1])) / accessor(arr[i - 1]))
//     }

//     return acc
//   }, [])
// }

function rollingMean(values) {
  return values.reduce((acc, cur, i, arr) => {
    if (i > N) {
      acc.push({
        ...cur,
        rollingMean: mean(arr.slice(Math.max(0, i - N), i).map((d) => d.mean)),
        topError: mean(
          arr.slice(Math.max(0, i - N), i).map((d) => d.mean + d.sem)
        ),
        bottomError: mean(
          arr.slice(Math.max(0, i - N), i).map((d) => d.mean - d.sem)
        ),
      });
    }

    return acc;
  }, []);
}

const MobilityChart = ({
  data,
  title,
  note,
  source,
  progress,
  selected,
  axisLabel,
  xAxisLabel,
  legendHeader,
  legendValues,
  jakartaEvents,
  wealthLabels,
  months,
}) => {
  // const { ref, width, height, windowSize, inView } = useVizualisation()
  const { ref, width, windowSize } = useVizualisation();

  // const height = Math.min(800, Math.max(windowSize.height - 200, 600))
  const height = Math.min(800, Math.max(windowSize.height - 200, 400));

  const events = Object.entries(jakartaEvents).map(([key, value]) => ({
    policy: value,
    date: Date.parse(key),
  }));

  const commutingData = commuting_json.map((d) => ({
    ...d,
    date: Date.parse(d.date),
    dateObject: new Date(Date.parse(d.date)),
  }));

  const homeData = home_json.map((d) => ({
    ...d,
    date: Date.parse(d.date),
    dateObject: new Date(Date.parse(d.date)),
  }));

  const lowWealthData = low_wealth_json.map((d) => ({
    ...d,
    date: Date.parse(d.date),
    dateObject: new Date(Date.parse(d.date)),
  }));

  const dataSets = [commutingData, homeData, lowWealthData];

  const selectedData = selected;

  const wealthLabel =
    selectedData < 2 ? "wealth_label_home" : "wealth_label_work";

  const rollingCommuting = rollingMean(
    dataSets[selectedData].filter((d) => d[wealthLabel] === "Low (0%-40%)")
  );

  const chartData = groups(
    dataSets[selectedData],
    (d) => d[wealthLabel]
  ).map((d) => rollingMean(d[1]));

  const high = interpolateDiscrete(chartData[0].map((d) => d.rollingMean));
  const low = interpolateDiscrete(chartData[1].map((d) => d.rollingMean));
  const medium = interpolateDiscrete(chartData[2].map((d) => d.rollingMean));

  const ips = [high, low, medium];

  const dateIp = interpolateDiscrete(chartData[0].map((d) => d.date));
  let currentDate = new Date(dateIp(progress)).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  currentDate = currentDate.replace(
    currentDate.substring(0, 3),
    months[currentDate.substring(0, 3)]
  );

  const w = 950 - margin.left - margin.right;
  const h = height - margin.top - margin.bottom;

  const x = scaleLinear()
    .domain(extent(rollingCommuting, (d) => d.date))
    .range([0, w]);

  const xp = scaleLinear().domain([0, 1]).range([0, w]);

  const y = scaleLinear()
    // .domain(extent(commuting.map(d => extent(d, d => d.rollingMean)).flat()))
    .domain(
      extent(
        chartData
          .map((d) =>
            d.map((d) => d.bottomError).concat(d.map((d) => d.topError))
          )
          .flat()
      )
    )
    .range([h - 50, 20]);

  const l = line()
    .x((d) => x(d.date))
    .y((d) => y(d.rollingMean))
    .curve(curveLinear);

  const l2 = line()
    .x((d) => x(d.date))
    .y((d) => y(d.value))
    .curve(curveLinearClosed);

  const c = scaleOrdinal()
    .domain(["Low (0%-40%)", "Medium (40%-80%)", "High (80%-100%)"])
    .range(["#EE6047", "#8E9598", "#481F7C"]);

  const c2 = scaleOrdinal()
    .domain(["Low", "Medium", "High"])
    .range(["#EE6047", "#8E9598", "#481F7C"]);

  return (
    <Vizualisation
      title={title}
      note={note}
      source={source}
      ref={ref}
      size="large"
    >
      <svg viewBox={`0 0 950 ${height}`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {y
            .nice()
            .ticks(5)
            .map((d) => (
              <text
                dx={-10}
                className={s.tick}
                y={y(d)}
                key={`tick-${d}`}
                style={{ fontSize: width < 600 ? 18 : 12 }}
              >
                {100 * d}
              </text>
            ))}
          <text
            transform={`translate(${x.range()[0]}, ${
              (y.range()[0] + y.range()[1]) / 2
            }) rotate(-90)`}
            dy={-20}
            className={s.axisLabel}
            style={{ fontSize: width < 600 ? 24 : 14 }}
          >
            {axisLabel}
          </text>
          <text
            x={(x.range()[0] + x.range()[1]) / 2}
            y={y.range()[0]}
            dy={20}
            className={s.axisLabel}
            style={{ fontSize: width < 600 ? 24 : 14 }}
          >
            {xAxisLabel}
          </text>

          <g
            transform={`translate(${x.range()[1]}, ${y.range()[0]})`}
            className="legend"
          >
            <text
              dx={0}
              style={{ textAnchor: "end", fontSize: width < 600 ? 24 : 14 }}
              y={-120}
            >
              {legendHeader}
            </text>
            {[0, 40, 80, 100].map((d) => (
              <text
                y={-d}
                dx={3}
                className={s.legendTick}
                key={`legend-tick-${d}`}
                style={{ fontSize: width < 600 ? 18 : 12 }}
              >
                {d}%
              </text>
            ))}
            {[
              [0, 40],
              [40, 80],
              [80, 100],
            ]
              .map((d, i) => ({
                range: d,
                name: legendValues[i],
              }))
              .map((d, i) => (
                <g
                  transform={`translate(${0}, ${-d.range[1]})`}
                  key={`legend-item-${i}`}
                >
                  <rect
                    x={-20}
                    width={20}
                    height={d.range[1] - d.range[0]}
                    fill={c2(d.name)}
                    stroke="white"
                  />
                  <text
                    className={s.legendLabel}
                    fill={c2(d.name)}
                    x={-20}
                    dx={-3}
                    style={{
                      fontSize:
                        i === 1
                          ? width < 600
                            ? 20
                            : 12
                          : width < 600
                          ? 24
                          : 14,
                      fontWeight: i === 1 ? "normal" : "bold",
                    }}
                    dy={(d.range[1] - d.range[0]) / 2}
                  >
                    {d.name}
                  </text>
                </g>
              ))}
          </g>

          <clipPath id={`mask-${selected}`}>
            <rect width={w * progress} height={h} />
          </clipPath>
          {chartData.map((g, i) => (
            <g clipPath={`url(#mask-${selected})`} key={`line-${i}`}>
              <path
                d={l2(
                  g
                    .map((d) => ({ date: d.date, value: d.topError }))
                    .concat(
                      g
                        .map((d) => ({ date: d.date, value: d.bottomError }))
                        .reverse()
                    )
                )}
                fill={c(g[0][wealthLabel])}
                opacity={0.15}
              />
              <path
                d={l(g)}
                fill="none"
                stroke={c(g[0][wealthLabel])}
                style={{
                  strokeDasharray:
                    g[0][wealthLabel] === "Medium (40%-80%)" ? "2 3" : "none",
                  strokeWidth:
                    g[0][wealthLabel] !== "Medium (40%-80%)" ? 1.5 : 1,
                }}
              />
            </g>
          ))}
          {events
            .filter(
              (d) =>
                d.date <=
                x.domain()[0] + (x.domain()[1] - x.domain()[0]) * progress
            )
            .map((d, i) => (
              <g key={`event-${i}`} transform={`translate(${x(d.date)}, ${0})`}>
                <line
                  y2={y.range()[0]}
                  stroke="#8E9598"
                  style={{ strokeDasharray: "1 3" }}
                />
                <text
                  transform="rotate(-90) translate(0, 0)"
                  style={{
                    fontSize: width < 600 ? 18 : 12,
                    textAnchor: "end",
                    strokeWidth: 2,
                  }}
                  fill="white"
                  stroke="white"
                  dy={-5}
                >
                  {d.policy}
                </text>
                <text
                  transform="rotate(-90) translate(0, 0)"
                  style={{ fontSize: width < 600 ? 18 : 12, textAnchor: "end" }}
                  dy={-5}
                >
                  {d.policy}
                </text>
              </g>
            ))}
          {progress > 0 &&
            chartData.map((g, i) => (
              <text
                key={`line-label-${i}`}
                dx={4}
                x={xp(progress)}
                y={y(ips[i](progress))}
                style={{
                  fontSize:
                    g[0][wealthLabel] === "Medium (40%-80%)"
                      ? width < 600
                        ? 20
                        : 14
                      : width < 600
                      ? 24
                      : 14,
                  dominantBaseline: "central",
                  fontWeight:
                    g[0][wealthLabel] === "Medium (40%-80%)"
                      ? "normal"
                      : "bold",
                }}
                fill={c(g[0][wealthLabel])}
              >
                {wealthLabels[g[0][wealthLabel].split(" ")[0]]}
              </text>
            ))}
          {progress > 0 && (
            <text
              x={xp(progress)}
              y={Math.max(...[0, 1, 2].map((d) => y(ips[d](progress)))) + 40}
              style={{
                fontWeight: "bold",
                fontSize: width < 600 ? 28 : 16,
              }}
            >
              {currentDate}
            </text>
          )}
        </g>
      </svg>
    </Vizualisation>
  );
};

export default MobilityChart;
